import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  Check,
  Users,
  ClipboardList,
  ArrowRight,
  TrendingUp,
  GraduationCap,
  PlaySquare,
  UserRound,
  ClipboardCheck,
  UserCheck,
  Route as RouteIcon,
  RefreshCw,
  User,
  CheckCircle2,
  CheckCheck,
  Quote,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { MinimalFooter } from "@/components/feo/MinimalFooter";
import { B2bLeadDialog } from "@/components/b2b/B2bLeadDialog";
import logoFeo from "@/assets/feo/logo-feo.png";
import graficoCompetencias from "@/assets/b2b/feoclinicas-dashboard-v3.webp.asset.json";

import naoEstaSozinho from "@/assets/b2b/feoclinicas-nao-esta-sozinho.jpg.asset.json";
import depoKarina from "@/assets/b2b/karina.webp.asset.json";
import depoAndre from "@/assets/b2b/andre.webp.asset.json";
import depoRenato from "@/assets/b2b/renato.webp.asset.json";
import depoRoberta from "@/assets/b2b/roberta.webp.asset.json";
import depoDanilo from "@/assets/b2b/danilo.webp.asset.json";
import { B2B_PLANOS, B2B_NUMEROS, B2B_FAQ, type PlanoLead } from "@/lib/b2b/config";
import { trackButtonClick } from "@/lib/tracking";

const TITLE = "Fisio em Ortopedia para Clínicas";
const URL = "https://fisioemortopedia.com.br/feoclinicas";
const SOCIAL_IMAGE =
  "https://storage.googleapis.com/gpt-engineer-file-uploads/uJh6Ec5dB7QIUilgPzSo9fT36WL2/social-images/social-1783178899843-ChatGPT_Image_4_de_jul._de_2026,_12_28_08.webp";
const DESC =
  "Capacitação continuada para equipes de fisioterapia musculoesquelética: diagnóstico da clínica, planos de estudo e mais de 120 cursos.";

export const Route = createFileRoute("/feoclinicas")({
  head: () => ({
    meta: [
      { title: TITLE },
      { name: "description", content: DESC },
      { property: "og:title", content: TITLE },
      { property: "og:description", content: DESC },
      { property: "og:url", content: URL },
      { property: "og:type", content: "website" },
      { property: "og:image", content: SOCIAL_IMAGE },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: TITLE },
      { name: "twitter:description", content: DESC },
      { name: "twitter:image", content: SOCIAL_IMAGE },
    ],
    links: [{ rel: "canonical", href: URL }],
  }),
  component: B2bPage,
});


function B2bPage() {
  const [openPlano, setOpenPlano] = useState<PlanoLead | null>(null);
  const [fisioCount, setFisioCount] = useState(5);

  /** Abre o formulário e registra o clique no CTA para o painel /admin/feoclinicas. */
  function openLead(plano: PlanoLead, label: string) {
    setOpenPlano(plano);
    void trackButtonClick({ buttonId: `b2b-cta-${plano.toLowerCase()}`, buttonLabel: label, path: "/feoclinicas" });
  }

  function formatCurrency(value: number) {
    return value.toLocaleString("pt-BR", { style: "currency", currency: "BRL", maximumFractionDigits: 0 });
  }

  return (
    <div className="min-h-screen overflow-x-clip bg-fc-navy text-white">


      {/* HERO — mesh gradient + mockups (desktop com app + celular sobreposto) */}
      <section className="relative overflow-hidden">
        {/* mesh gradient navy + teal + azul, sem roxo */}
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(58% 58% at 6% 10%, rgba(63,213,201,0.34) 0%, transparent 66%)," +
              "radial-gradient(46% 46% at 36% 0%, rgba(255,255,255,0.12) 0%, transparent 70%)," +
              "radial-gradient(52% 52% at 70% 6%, rgba(59,130,246,0.28) 0%, transparent 70%)," +
              "radial-gradient(56% 56% at 98% 20%, rgba(63,213,201,0.18) 0%, transparent 72%)," +
              "radial-gradient(84% 62% at 50% 82%, rgba(11,18,32,0.0) 0%, transparent 76%)",
          }}
        />
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-px"
          style={{ background: "linear-gradient(90deg, transparent, rgba(63,213,201,0.45), transparent)" }}
        />

        <div className="relative mx-auto max-w-6xl px-4 pt-10 sm:px-6 sm:pt-16">
          <div className="mx-auto max-w-5xl text-center">
            <img decoding="async" src={logoFeo} alt="Fisio em Ortopedia" className="mx-auto mb-8 h-9 w-auto sm:h-11" />
            <p className="inline-flex items-center gap-2 rounded-full border border-fc-teal/25 bg-fc-teal/[0.08] px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.24em] text-fc-teal backdrop-blur-sm sm:text-[11px]">
              PROGRAMA FEO PARA CLÍNICAS
            </p>
            <h1 className="mt-6 text-balance text-[clamp(1.5rem,6vw,3.4rem)] font-black leading-[1.05] tracking-tight">
              <span className="block sm:whitespace-nowrap">Chega de treinamentos soltos.</span>
              <span className="block sm:whitespace-nowrap">
                Leve toda a{" "}
                <span className="bg-gradient-to-r from-fc-teal to-fc-blue bg-clip-text text-transparent">
                  estrutura da FEO
                </span>
              </span>
              <span className="block sm:whitespace-nowrap">para dentro da <span className="text-fc-teal">sua clínica.</span></span>
            </h1>


            <p className="mx-auto mt-6 max-w-2xl text-balance text-base leading-relaxed text-white/75 sm:text-lg">
              Toda a sua equipe com acesso a pós-graduações, cursos e encontros ao vivo que já
              impactaram a carreira de +45 mil fisioterapeutas por todo Brasil.
            </p>
            <div className="mt-9 flex justify-center">
              <Button
                size="lg"
                asChild
                className="rounded-full bg-fc-teal px-7 font-bold text-fc-navy shadow-[0_14px_44px_-14px_rgba(63,213,201,0.65)] transition-all hover:bg-fc-teal/90 hover:shadow-[0_18px_54px_-14px_rgba(63,213,201,0.8)]"
              >
                <a href="#planos">Quero levar a FEO para minha clínica <ArrowRight className="ml-1.5 h-4 w-4" /></a>
              </Button>
            </div>

          </div>

          {/* Mockups: relatório dentro da moldura desktop + plano de ação no celular */}
          <div className="relative mt-12 pt-28 sm:mt-16 sm:pt-0">
            {/* moldura desktop */}
            <div className="mx-auto max-w-4xl overflow-hidden rounded-t-[22px] border border-white/12 border-b-0 bg-fc-surface/80 shadow-[0_-10px_120px_-30px_rgba(63,213,201,0.32),0_50px_120px_-50px_rgba(0,0,0,0.9)] backdrop-blur-md sm:rounded-t-[28px]">
              <div className="flex items-center gap-3 border-b border-white/10 bg-white/[0.05] px-4 py-3 sm:px-6">
                <img loading="lazy" decoding="async" src={logoFeo} alt="" aria-hidden="true" className="h-5 w-auto sm:h-6" />
                <span className="ml-auto flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-full bg-white/20" />
                  <span className="h-2 w-2 rounded-full bg-white/20" />
                  <span className="h-2 w-2 rounded-full bg-fc-teal/60" />
                </span>
              </div>
              <div className="overflow-hidden bg-white">
                <img loading="lazy" decoding="async"
                  src={graficoCompetencias.url}
                  alt="Plataforma da FEO com as pós-graduações, certificações e cursos liberados para a equipe da clínica"
                  className="block w-full"
                />
              </div>
            </div>

            {/* moldura celular sobreposta (base reta, tela "descendo" da página) — app de avaliação */}
            <div className="absolute bottom-0 right-0 w-[38%] max-w-[230px] sm:right-2 md:right-6">
              <div className="overflow-hidden rounded-t-[22px] border-[5px] border-b-0 border-fc-surface bg-fc-surface shadow-[0_30px_70px_-25px_rgba(0,0,0,0.95)] sm:rounded-t-[28px] sm:border-[7px] sm:border-b-0">
                <div className="relative bg-[#f8fafc] pb-6">
                  {/* notch */}
                  <span className="absolute left-1/2 top-0 z-10 h-4 w-16 -translate-x-1/2 rounded-b-xl bg-fc-surface" />

                  {/* header do app */}
                  <div className="flex items-center justify-between px-3 pb-3 pt-6">
                    <span className="flex h-5 w-5 items-center justify-center rounded-md bg-fc-navy text-[7px] font-extrabold text-fc-teal">
                      FEO
                    </span>
                    <span className="h-5 w-5 rounded-full border border-slate-300 bg-slate-200" />
                  </div>

                  <div className="px-3">
                    <p className="text-[7px] font-bold uppercase tracking-[0.14em] text-fc-blue">
                      Relatório individual
                    </p>
                    <p className="text-sm font-extrabold leading-tight text-fc-navy">Avaliação</p>

                    {/* score */}
                    <div className="mt-2 rounded-xl border border-slate-100 bg-white p-2 shadow-sm">
                      <div className="flex items-end justify-between gap-2">
                        <div>
                          <p className="text-[7px] font-medium text-slate-500">Score geral</p>
                          <p className="text-lg font-extrabold leading-none text-fc-navy">
                            84<span className="text-[10px] font-normal text-slate-400">/100</span>
                          </p>
                        </div>
                        <span className="rounded-full bg-fc-teal/15 px-1.5 py-0.5 text-[6px] font-bold text-[#0d9488]">
                          +12% vs média
                        </span>
                      </div>
                    </div>

                    {/* radar */}
                    <div className="relative mt-3 aspect-square w-full">
                      <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full text-slate-200">
                        <circle cx="50" cy="50" r="45" fill="none" stroke="currentColor" strokeWidth="0.5" />
                        <circle cx="50" cy="50" r="30" fill="none" stroke="currentColor" strokeWidth="0.5" />
                        <circle cx="50" cy="50" r="15" fill="none" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="50" y1="5" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="93" y1="36" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="76" y1="86" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="24" y1="86" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" />
                        <line x1="7" y1="36" x2="50" y2="50" stroke="currentColor" strokeWidth="0.5" />
                        <polygon
                          points="50,15 85,40 70,75 35,70 20,35"
                          fill="rgba(63, 213, 201, 0.22)"
                          stroke="#3fd5c9"
                          strokeWidth="2"
                          strokeLinejoin="round"
                        />
                        <circle cx="50" cy="15" r="2" fill="#3fd5c9" />
                        <circle cx="85" cy="40" r="2" fill="#3fd5c9" />
                        <circle cx="70" cy="75" r="2" fill="#3fd5c9" />
                        <circle cx="35" cy="70" r="2" fill="#3fd5c9" />
                        <circle cx="20" cy="35" r="2" fill="#3fd5c9" />
                      </svg>
                      <span className="absolute -top-1 left-1/2 -translate-x-1/2 text-[6px] font-bold uppercase tracking-tight text-slate-500">
                        Raciocínio clínico
                      </span>
                      <span className="absolute right-[-6px] top-[32%] text-[6px] font-bold uppercase tracking-tight text-slate-500">
                        Avaliação
                      </span>
                      <span className="absolute bottom-[6%] right-[-4px] text-[6px] font-bold uppercase tracking-tight text-slate-500">
                        Tratamento
                      </span>
                      <span className="absolute bottom-[6%] left-[-2px] text-[6px] font-bold uppercase tracking-tight text-slate-500">
                        PBE
                      </span>
                      <span className="absolute left-[-6px] top-[32%] text-[6px] font-bold uppercase tracking-tight text-slate-500">
                        Soft skill
                      </span>
                    </div>

                    {/* legenda */}
                    <div className="mt-3 grid grid-cols-2 gap-1.5">
                      <div className="flex items-center gap-1.5 rounded-lg border border-slate-100 bg-white p-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-fc-teal" />
                        <span className="text-[6px] font-semibold text-slate-600">Excelente</span>
                      </div>
                      <div className="flex items-center gap-1.5 rounded-lg border border-slate-100 bg-white p-1.5">
                        <span className="h-1.5 w-1.5 rounded-full bg-fc-blue" />
                        <span className="text-[6px] font-semibold text-slate-600">Meta do time</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </div>

        {/* fade para o fundo escuro da próxima seção */}
        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-b from-transparent to-fc-navy" />
      </section>

      {/* NÚMEROS */}
      <section className="relative border-t border-white/[0.07] bg-white/[0.015]">
        <div className="mx-auto max-w-6xl px-4 py-12 sm:px-6 sm:py-14">
          <dl className="grid grid-cols-2 gap-x-6 gap-y-8 sm:grid-cols-4 sm:divide-x sm:divide-white/[0.08]">
            {B2B_NUMEROS.map((n) => (
              <div key={n.label} className="text-center sm:px-4">
                <dt className="text-3xl font-black tracking-tight text-fc-teal sm:text-4xl">
                  {n.valor}
                </dt>
                <dd className="mx-auto mt-2 max-w-[14rem] text-[10px] font-bold uppercase leading-relaxed tracking-[0.16em] text-white/55 sm:text-[11px]">
                  {n.label}
                </dd>
              </div>
            ))}
          </dl>
        </div>
      </section>


      {/* DORES DO GESTOR */}
      <section className="border-t border-slate-200 bg-slate-50">
        <div className="mx-auto max-w-4xl px-4 py-14 sm:px-6 sm:py-16">
          <div className="mb-8 text-center">
            <p className="text-[11px] font-black uppercase tracking-[0.24em] text-fc-blue sm:text-xs">
              Essas frases já passaram na sua cabeça?
            </p>
            <h2 className="mx-auto mt-4 max-w-4xl text-balance text-center text-2xl font-bold leading-snug tracking-tight text-slate-900 sm:text-3xl">
              <span className="block lg:whitespace-nowrap">{"\u201CEu sei o que deveria estar fazendo."}</span>
              <span className="block lg:whitespace-nowrap">{"Mas sempre tem alguma coisa mais urgente para resolver.\u201D"}</span>
            </h2>
          </div>

          <div className="space-y-3">
            {[
              {
                t: "A clínica ainda depende demais de mim",
                d: "Casos difíceis e decisões técnicas continuam voltando para você.",
              },
              {
                t: "Já investi em treinamentos e não sei se valeu a pena",
                d: "A equipe participa, mas você não consegue enxergar o que mudou.",
              },
              {
                t: "Eu não sei se todos da equipe estão no nível que a clínica exige.",
                d: "Currículo e experiência não mostram se cada profissional entrega o padrão que se espera.",
              },
              {
                t: "Quero crescer sem perder o controle da qualidade",
                d: "Mais pacientes exigem um padrão técnico que cresça junto.",
              },
            ].map((c) => (
              <div key={c.t} className="group relative">
                <div className="absolute -inset-0.5 rounded-xl bg-fc-teal/15 opacity-0 blur-[2px] transition duration-500 group-hover:opacity-100" />
                <div className="relative flex items-start gap-4 rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
                  <Quote className="mt-0.5 h-6 w-6 shrink-0 text-fc-teal/60" />
                  <div>
                    <p className="text-lg font-medium leading-snug text-slate-900 sm:text-xl">
                      {"\u201C"}
                      {c.t}
                      {"\u201D"}
                    </p>
                    <p className="mt-1 text-sm leading-relaxed text-slate-500">{c.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-center text-sm italic leading-relaxed text-slate-500">
            Quanto mais a clínica cresce,{" "}
            <span className="font-semibold not-italic text-fc-blue">
              mais difícil fica garantir o que está sendo entregue
            </span>{" "}
            por cada profissional.
          </p>
        </div>
      </section>

      {/* POSICIONAMENTO */}
      <section className="border-t border-white/[0.07]">
        <div className="mx-auto grid max-w-6xl grid-cols-1 items-center gap-8 px-4 py-20 sm:px-6 sm:py-25 md:grid-cols-2 md:gap-12">
          <div className="relative">
            <div
              className="pointer-events-none absolute -inset-y-6 inset-x-0 sm:-inset-6"
              style={{
                background:
                  "radial-gradient(55% 55% at 50% 50%, rgba(63,213,201,0.18) 0%, transparent 72%)",
              }}
            />
            <img
              src={naoEstaSozinho.url}
              alt="Você não está sozinho: muitos gestores de clínica passam pelo mesmo problema"
              loading="lazy"
              width={1080}
              height={1080}
              className="relative block aspect-square w-full rounded-3xl border border-white/[0.08] object-cover"
            />
          </div>

          <div className="flex min-h-0 flex-col justify-center">
            <h2 className="text-2xl font-black leading-tight tracking-tight sm:text-4xl">
              O problema não é falta de gestão.{" "}
              <span className="text-fc-teal">
                É que a equipe ainda depende demais de você.
              </span>
            </h2>
            <p className="mt-6 text-sm leading-relaxed text-white/70 sm:text-base">
              Quando a evolução dos fisioterapeutas depende da sua percepção, do seu tempo e de
              treinamentos pontuais, alguns sinais começam a aparecer:
            </p>

            <ul className="mt-5 space-y-2.5">
              {[
                "Você continua sendo a referência para decisões importantes",
                "O desenvolvimento da equipe perde espaço para a operação",
                "Fica difícil saber o nível técnico real de cada profissional",
                "A qualidade começa a depender demais da sua presença",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-fc-teal" />
                  <span className="text-sm leading-relaxed text-white/80 sm:text-[15px]">
                    {item}
                  </span>
                </li>
              ))}
            </ul>

            <p className="mt-6 text-sm leading-relaxed text-white/70 sm:text-base">
              O <strong className="font-bold text-white">FEO para Clínicas</strong> existe para tirar
              esse peso das suas costas e dar continuidade ao desenvolvimento da equipe.
            </p>
          </div>
        </div>
      </section>

      {/* ECOSSISTEMA */}
      <section className="relative overflow-hidden border-t border-fc-teal/15 bg-[#062421]">
        {/* sombra verde — véus de teal sobre o petróleo escuro */}
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 55% at 50% 0%, rgba(63,213,201,0.20), transparent 70%)," +
              "radial-gradient(ellipse 60% 60% at 10% 100%, rgba(63,213,201,0.10), transparent 70%)," +
              "radial-gradient(ellipse 50% 50% at 95% 85%, rgba(59,130,246,0.08), transparent 70%)",
          }}
        />

        <div className="relative mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-fc-teal/30 bg-fc-teal/[0.10] px-4 py-1.5 text-[11px] font-black uppercase tracking-[0.24em] text-fc-teal">
              Sua clínica com 100% da FEO
            </span>
          </div>

          <h2 className="mx-auto mt-5 max-w-3xl text-center text-2xl font-black leading-tight tracking-tight sm:text-4xl">
            <span className="block text-white">Todas as formações da FEO</span>
            <span className="mt-1 block bg-gradient-to-r from-fc-teal via-[#7ff0e4] to-fc-blue bg-clip-text text-transparent">
              disponíveis para toda sua equipe.
            </span>
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-center text-sm leading-relaxed text-white/70 sm:text-base">
            Todos os fisioterapeutas têm acesso completo aos nossos cursos. Você escolhe se eles estudam com autonomia ou se a FEO acompanha de perto o seu time.
          </p>

          <div className="mt-10 grid gap-4 md:grid-cols-3">
            {[
              {
                icon: GraduationCap,
                t: "6 pós-graduações",
                d: "Formações Lato Sensu, 100% online, reconhecidas pelo MEC para fortalecer o padrão técnico do seu time.",
              },
              {
                icon: PlaySquare,
                t: "+120 cursos",
                d: "Conteúdos práticos e atualizados mensalmente para apoiar a tomada de decisão no dia a dia da clínica.",
              },
              {
                icon: Users,
                t: "Discussões Clínicas Ao Vivo",
                d: "Encontros mensais para discutir casos, atualizar condutas e aproximar ciência da prática clínica.",
              },
            ].map((c) => (
              <div
                key={c.t}
                className="rounded-2xl border border-white/[0.10] bg-white/[0.04] p-6 text-center transition-colors duration-300 hover:border-fc-teal/30 hover:bg-white/[0.07]"
              >
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full border border-fc-teal/25 bg-fc-teal/10">
                  <c.icon className="h-7 w-7 text-fc-teal" />
                </span>
                <h3 className="mt-5 text-lg font-bold">{c.t}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/70">{c.d}</p>
              </div>
            ))}
          </div>

          <p className="mt-14 text-center text-[11px] font-black uppercase tracking-[0.24em] text-fc-teal/80 sm:text-xs">
            E para um acompanhamento mais próximo
          </p>

          <div className="mt-5 rounded-3xl border border-fc-teal/40 bg-black/40 p-6 shadow-[0_24px_80px_-40px_rgba(63,213,201,0.55)] ring-1 ring-fc-teal/10 sm:p-10">
            <p className="mx-auto max-w-3xl text-center text-base font-black leading-snug sm:text-xl">
              A FEO também pode direcionar o{" "}
              <span className="text-fc-teal">desenvolvimento individual</span>.
            </p>

            <div className="mt-9 grid gap-5 md:grid-cols-3">
              {[
              {
                  icon: ClipboardList,
                  t: "Diagnóstico da clínica",
                  d: "Avaliação das competências da equipe para identificar pontos fortes e prioridades de desenvolvimento.",
                },
                {
                  icon: UserRound,
                  t: "Trilhas de Estudos",
                  d: "Cada fisioterapeuta recebe uma trilha de estudos alinhada às suas necessidades e às prioridades da clínica.",
                },
                {
                  icon: TrendingUp,
                  t: "Reavaliações",
                  d: "Acompanhamento periódico da evolução da equipe e adequação das trilhas de estudos individuais.",
                },
              ].map((c) => (
                <div
                  key={c.t}
                  className="rounded-2xl border border-fc-teal/20 bg-fc-teal/[0.06] p-5 text-center transition-colors duration-300 hover:border-fc-teal/40 hover:bg-fc-teal/[0.10] sm:p-6"
                >
                  <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full border border-fc-teal/30 bg-fc-teal/10">
                    <c.icon className="h-6 w-6 text-fc-teal" strokeWidth={1.75} />
                  </span>
                  <h3 className="mt-4 text-sm font-bold leading-snug sm:text-base">{c.t}</h3>
                  <p className="mx-auto mt-2 max-w-xs text-sm leading-relaxed text-white/75">
                    {c.d}
                  </p>
                </div>
              ))}
            </div>
          </div>

          <p className="mt-10 text-center text-sm font-bold sm:text-base">
            Sua equipe ganha estrutura.{" "}
            <span className="text-fc-teal">Sua clínica decide o quanto quer acompanhar isso de perto.</span>
          </p>
        </div>
      </section>


      {/* PROVA REAL */}
      <section className="border-t border-white/[0.07]">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <p className="text-center text-[11px] font-black uppercase tracking-[0.24em] text-fc-teal sm:text-xs">
            DEPOIMENTOS
          </p>
          <h2 className="mx-auto mt-4 max-w-5xl text-center text-2xl font-black leading-tight tracking-tight sm:text-4xl">
            Mais de 200 clínicas já colocaram o desenvolvimento da equipe nas mãos da FEO.
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-center text-sm leading-relaxed text-white/70 sm:text-base">
            Veja o que muda na rotina de quem decidiu estruturar o desenvolvimento da equipe.
          </p>

          {(() => {
            const depos = {
              d1: {
                img: depoKarina.url,
                nameW: "w-28",
                role: "Gestora de Clínica",
                time: "10:44",
                p: [
                  "Bom dia. Guilherme tdo bem? passando para falar que fizemos as reuniões individuais e geral. E foi ótimo!! Foi nossa melhor reunião geral até hoje. Estamos amando o projeto!",
                  "Queria saber se todo o dia 10 que vamos receber os retornos de vocês. Porque já estou agendando a prox reunião geral aqui com o nosso time.",
                ],
              },
              d2: {
                img: depoAndre.url,
                nameW: "w-36",
                role: "Gestor de Clínica",
                time: "09:15",
                p: [
                  "Primeiramente, gostaria de agradecer e parabenizar pela constante evolução do Programa FEO para Clínicas. Nós, da Reactive, estamos muito satisfeitos com a mentoria e com as ferramentas que vêm sendo continuamente disponibilizadas. Sentimos que existe uma escuta ativa da equipe e uma real preocupação em atender às nossas necessidades com precisão e qualidade.",
                ],
              },
              d3: {
                img: depoRenato.url,
                nameW: "w-24",
                role: "Gestor de Clínica",
                time: "14:20",
                p: [
                  "Ao meu ver, de forma geral, o Programa tem sido um aliado importante no processo de padronização clínica, qualificação técnica e desenvolvimento dos fisioterapeutas. As trilhas agregaram ainda mais às demandas do dia a dia.",
                ],
              },
              d4: {
                img: depoRoberta.url,
                nameW: "w-32",
                role: "Gestora de Clínica",
                time: "11:30",
                p: [
                  "O programa é excelente, acredito que as abordagens trazidas agregaram muito na nossa forma como atuar frente a gestão. Não só como atender o paciente, mas também como gerir os fisioterapeutas.",
                  "Os temas abordados estão excelentes, e a apresentação de material de visualização no vídeo torna tudo mais atrativo e mais fácil para aprender.",
                ],
              },
              d5: {
                img: depoDanilo.url,
                nameW: "w-36",
                role: "Gestor de Clínica",
                time: "17:05",
                p: [
                  "Equipe/coordenação sempre disposta a nos ajudar e sanar nossas dúvidas. Além disso, o programa foi muito bem pensado e estruturado, professores de referência são os diferenciais. Acompanhamento do Guilherme é excepcional, sempre gentil e atencioso com nossas demandas. Só agradecer por ter a possibilidade de aprender e crescer com os melhores!",
                ],
              },
            };

            type Depo = (typeof depos)["d1"];

            const Card = ({ d }: { d: Depo }) => (
              <figure
                className="rounded-[26px] border border-fc-teal/35 bg-fc-teal/[0.06] p-5 shadow-[0_36px_90px_-40px_rgba(63,213,201,0.4)] backdrop-blur-sm transition-colors duration-300 sm:p-6"
              >
                <div className="flex items-center gap-3">
                  <img
                    src={d.img}
                    alt={`Gestor de clínica que participa do programa FEO para Clínicas, ${d.role}`}
                    loading="lazy"
                    width={512}
                    height={512}
                    className="h-11 w-11 shrink-0 rounded-full object-cover ring-2 ring-fc-teal/60"
                  />
                  <div className="min-w-0">
                    <span
                      aria-hidden="true"
                      className={`block h-3 rounded-full bg-fc-teal/35 blur-[3px] ${d.nameW}`}
                    />
                    <p className="mt-1.5 text-[10px] font-bold uppercase tracking-[0.16em] text-fc-teal">
                      {d.role}
                    </p>
                  </div>
                </div>

                <div className="mt-4 space-y-2">
                  {d.p.map((line, i) => (
                    <blockquote
                      key={i}
                      className="rounded-2xl rounded-tl-md bg-[#f4efe6] px-4 py-3 shadow-[0_6px_18px_-10px_rgba(0,0,0,0.6)]"
                    >
                      <p className="text-[13.5px] leading-relaxed text-[#1f2b25] sm:text-sm">
                        {line.split(/(Reactive)/g).map((part, j) =>
                          part === "Reactive" ? (
                            <span key={j} className="blur-[3px] select-none" aria-hidden="true">Reactive</span>
                          ) : (
                            <span key={j}>{part}</span>
                          ),
                        )}
                      </p>
                      {i === d.p.length - 1 && (
                        <span className="mt-1.5 flex items-center justify-end gap-1 text-[10px] text-[#1f2b25]/45">
                          {d.time}
                          <CheckCheck className="h-3 w-3 text-[#0f9c92]" />
                        </span>
                      )}
                    </blockquote>
                  ))}
                </div>
              </figure>
            );

            return (
              <div className="mt-12 grid items-start gap-6 sm:gap-8 lg:grid-cols-3">
                <div className="flex flex-col gap-6 sm:gap-8">
                  <Card d={depos.d1} />
                  <Card d={depos.d3} />
                </div>
                <div className="lg:pt-6">
                  <Card d={depos.d2} />
                </div>
                <div className="flex flex-col gap-6 sm:gap-8">
                  <Card d={depos.d4} />
                  <Card d={depos.d5} />
                </div>
              </div>
            );
          })()}



        </div>
      </section>

      {/* PLANOS */}

      <section id="planos" className="bg-[#F8F9FB]">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <h2 className="text-center text-2xl font-black leading-tight tracking-tight text-fc-navy sm:text-4xl">
            Escolha o plano ideal para a sua clínica
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-relaxed text-slate-500 sm:text-base">
            Duas formas de desenvolver sua equipe com a metodologia da FEO.
          </p>

          <div className="mt-12 grid items-stretch gap-5 md:grid-cols-2">
            {B2B_PLANOS.map((p, idx) => {
              const isLivre = idx === 0;
              const Icon = isLivre ? User : ClipboardCheck;
              const accent = isLivre ? "#34d399" : "#60a5fa";
              const accentDeep = isLivre ? "#10b981" : "#3b82f6";
              const tagline = isLivre
                ? "Autonomia completa para a sua equipe"
                : "Acompanhamento estratégico individual";
              const guiadoIcons = [ClipboardList, RouteIcon, RefreshCw];
              return (
                <div
                  key={p.id}
                  className={`relative flex flex-col rounded-3xl border bg-fc-navy p-6 transition-all duration-300 hover:border-opacity-40 sm:p-8 ${
                    isLivre
                      ? "border-emerald-500/20 shadow-[0_24px_60px_-30px_rgba(16,185,129,0.18)] hover:border-emerald-400/30"
                      : "border-blue-500/20 shadow-[0_24px_60px_-30px_rgba(59,130,246,0.18)] hover:border-blue-400/30"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <span
                      className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl border"
                      style={{ borderColor: `${accent}55`, background: `${accent}14` }}
                    >
                      <Icon className="h-5 w-5" style={{ color: accent }} />
                    </span>
                    <div className="min-w-0">
                      <h3 className="text-xl font-black sm:text-2xl">
                        Plano <span style={{ color: accent }}>{p.id}</span>
                      </h3>
                      <p className="mt-0.5 text-sm leading-snug text-white/60">{tagline}</p>
                    </div>
                  </div>

                  <div className="mt-6">
                    <div className="flex items-baseline gap-1.5">
                      <span className="text-lg font-bold" style={{ color: accent }}>
                        R$
                      </span>
                      <span className="text-4xl font-black leading-none tracking-tight text-white sm:text-5xl">
                        {p.precoMensalPorFisio}
                      </span>
                      <span className="text-sm font-semibold text-white/70">
                        /mês por fisio
                      </span>
                    </div>
                    <p className="mt-2 text-[10px] font-black uppercase tracking-[0.18em] text-white/45">
                      Assinatura Anual
                    </p>
                  </div>

                  <div className="my-6 h-px bg-white/[0.08]" />

                  {!isLivre && (
                    <div
                      className="mb-6 flex items-center gap-3 rounded-2xl border px-4 py-3"
                      style={{
                        borderColor: "#60a5fa40",
                        background: "#60a5fa14",
                      }}
                    >
                      <CheckCircle2 className="h-5 w-5 shrink-0" style={{ color: "#60a5fa" }} />
                      <p className="text-sm font-semibold text-white/90">
                        Inclui <span className="font-black text-white">TUDO</span>&nbsp;do Plano <span style={{ color: "#34d399" }}>Livre</span>
                      </p>
                    </div>
                  )}

                  <p className="text-[11px] font-black uppercase tracking-[0.18em] text-white/60">
                    {isLivre ? "O que está incluído:" : "Benefícios exclusivos do Guiado:"}
                  </p>

                  <ul className="mt-4 space-y-3.5">
                    {p.itens.map((i, itemIdx) => {
                      const ItemIcon = isLivre ? CheckCircle2 : guiadoIcons[itemIdx % guiadoIcons.length];
                      return (
                        <li key={i} className="flex items-center gap-3 text-sm leading-relaxed text-white/85">
                          <span
                            className="grid h-8 w-8 shrink-0 place-items-center rounded-xl border"
                            style={{ borderColor: `${accent}30`, background: `${accent}0d` }}
                          >
                            <ItemIcon className="h-4 w-4" style={{ color: accent }} />
                          </span>
                          <span>{i}</span>
                        </li>
                      );
                    })}
                  </ul>

                  <div aria-hidden className="min-h-10 grow" />
                  <div className="pt-0">
                    <Button
                      size="lg"
                      className={`w-full rounded-full font-bold transition-all ${
                        isLivre
                          ? "bg-emerald-400 text-fc-navy shadow-[0_14px_40px_-16px_rgba(52,211,153,0.55)] hover:bg-emerald-300"
                          : "bg-blue-500 text-white shadow-[0_14px_40px_-16px_rgba(59,130,246,0.55)] hover:bg-blue-400"
                      }`}
                      onClick={() => openLead(p.id, `${p.nome}, ${p.cta}`)}
                    >
                      {p.cta}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>


      {/* PARA QUEM É */}
      <section className="border-t border-white/[0.07] bg-fc-navy">
        <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-20">
          <div className="relative overflow-hidden rounded-3xl border border-fc-teal/20 bg-fc-surface/70 p-8 shadow-[0_30px_80px_-40px_rgba(63,213,201,0.35)] sm:p-12">
            {/* textura sutil */}
            <div
              className="pointer-events-none absolute inset-0"
              style={{
                backgroundImage:
                  "radial-gradient(circle at 15% 12%, rgba(63,213,201,0.20) 0%, transparent 45%), radial-gradient(circle at 88% 88%, rgba(59,130,246,0.16) 0%, transparent 48%)",
              }}
            />

            <div className="relative">
              <h2 className="mx-auto max-w-2xl text-center text-2xl font-black leading-tight tracking-tight sm:text-4xl">
                Se a sua clínica vive isso, o{" "}
                <span className="text-fc-teal">FEO para Clínicas</span> foi feito para você:
              </h2>

              <ul className="mx-auto mt-8 flex max-w-xl flex-col items-center space-y-4">
                {[
                  "A equipe cresce, mas tudo ainda volta para você.",
                  "Nem todos da equipe te dão a mesma segurança.",
                  "Você investe em treinamentos, mas não vê mudanças no time.",
                  "Falta clareza sobre onde cada profissional precisa evoluir.",
                  "O desenvolvimento só acontece quando sobra tempo.",
                ].map((item) => (
                  <li key={item} className="flex w-full items-start gap-3">
                    <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-fc-teal/30 bg-fc-teal/12">
                      <Check className="h-3.5 w-3.5 text-fc-teal" />
                    </span>
                    <span className="text-sm font-medium leading-relaxed text-white/80 sm:text-base">
                      {item}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CALCULADORA */}
      <section className="bg-[#F8F9FB]">
        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 sm:py-20">
          <h2 className="text-center text-2xl font-black leading-tight tracking-tight text-fc-navy sm:text-4xl">
            Simule o investimento para sua <span className="text-fc-teal">clínica</span>
          </h2>
          <p className="mx-auto mt-3 max-w-2xl text-center text-sm leading-relaxed text-slate-600 sm:text-base">
            Selecione o número de fisioterapeutas na sua equipe e veja o valor da assinatura anual.
          </p>

          <div className="mt-10 rounded-3xl border border-slate-100 bg-white p-6 shadow-lg shadow-slate-200/60 sm:p-10">
            <label className="block text-center text-[11px] font-black uppercase tracking-[0.18em] text-slate-500">
              Número de Fisioterapeutas
            </label>
            <div className="mt-6 flex items-center justify-center gap-4 sm:gap-6">
              <button
                type="button"
                onClick={() => setFisioCount((c) => Math.max(1, c - 1))}
                className="grid h-11 w-11 place-items-center rounded-full border-2 border-slate-200 text-xl text-slate-400 transition-colors hover:border-fc-teal hover:text-fc-teal sm:h-12 sm:w-12"
                aria-label="Diminuir"
              >
                −
              </button>
              <input
                type="number"
                min={1}
                max={100}
                value={fisioCount}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setFisioCount(Number.isNaN(v) ? 1 : Math.max(1, Math.min(100, v)));
                }}
                className="w-28 appearance-none bg-transparent text-center text-4xl font-black text-fc-navy focus:outline-none sm:w-32 sm:text-5xl"
              />
              <button
                type="button"
                onClick={() => setFisioCount((c) => Math.min(100, c + 1))}
                className="grid h-11 w-11 place-items-center rounded-full border-2 border-slate-200 text-xl text-slate-400 transition-colors hover:border-fc-teal hover:text-fc-teal sm:h-12 sm:w-12"
                aria-label="Aumentar"
              >
                +
              </button>
            </div>
            <input
              type="range"
              min={1}
              max={50}
              value={Math.min(50, fisioCount)}
              onChange={(e) => setFisioCount(parseInt(e.target.value, 10))}
              className="mx-auto mt-6 block h-2 w-full max-w-md cursor-pointer appearance-none rounded-full bg-slate-100 accent-fc-teal"
            />
          </div>

          <div className="mt-8 grid items-stretch gap-5 md:grid-cols-2">
            {[
              {
                id: "Livre" as const,
                lead: "Livre" as const,
                preco: 97,
                nome: "Plano Livre",
                tagline: "Autonomia completa para a sua equipe",
                cta: "Selecionar Plano Livre",
                isLivre: true,
              },
              {
                id: "Guiado" as const,
                lead: "Guiado" as const,
                preco: 297,
                nome: "Plano Guiado",
                tagline: "Acompanhamento estratégico individual",
                cta: "Selecionar Plano Guiado",
                isLivre: false,
              },
            ].map((p) => {
              const totalAnual = p.preco * fisioCount * 12;
              const parcela12x = totalAnual / 12;
              return (
                <div
                  key={p.id}
                  className="flex flex-col rounded-3xl border border-slate-100 bg-white p-6 shadow-lg shadow-slate-200/60 sm:p-8"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <h3 className="text-xl font-black text-fc-navy sm:text-2xl">{p.nome}</h3>
                      <p className="mt-0.5 text-sm text-slate-500">{p.tagline}</p>
                    </div>
                    <div className="text-right">
                      <div
                        className="text-sm font-bold"
                        style={{ color: p.isLivre ? "#00D492" : "#2B7FFF" }}
                      >
                        {formatCurrency(p.preco)}/mês
                      </div>
                      <div className="text-xs text-slate-400">por fisio</div>
                    </div>
                  </div>

                  <div className="my-6 h-px bg-slate-100" />

                  <div className="flex-1">
                    {/* Parcelado */}
                    <div>
                      <span
                        className="inline-block rounded px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.18em]"
                        style={{
                          color: p.isLivre ? "#00D492" : "#2B7FFF",
                          background: p.isLivre ? "rgba(0,212,146,0.12)" : "rgba(43,127,255,0.12)",
                        }}
                      >
                        Parcelado
                      </span>
                      <div className="mt-2 flex items-baseline gap-1 text-fc-navy">
                        <span className="text-lg font-bold">12x de</span>
                        <span className="text-4xl font-black tracking-tight sm:text-5xl">
                          {formatCurrency(parcela12x)}
                        </span>
                      </div>
                      <p className="mt-1 text-sm text-slate-500">Sem juros no cartão de crédito</p>
                    </div>

                    {/* Divisor OU */}
                    <div className="my-6 flex items-center gap-4">
                      <div className="h-px flex-1 bg-slate-100" />
                      <span className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">OU</span>
                      <div className="h-px flex-1 bg-slate-100" />
                    </div>

                    {/* À vista */}
                    <div>
                      <span className="block text-[10px] font-black uppercase tracking-[0.18em] text-slate-400">
                        À vista
                      </span>
                      <div className="mt-2 text-2xl font-black text-fc-navy sm:text-3xl">
                        {formatCurrency(totalAnual)}
                      </div>
                    </div>
                  </div>

                  <div className="mt-8">
                    <Button
                      size="lg"
                      className="w-full rounded-full font-bold text-white transition-all hover:opacity-90"
                      style={{ background: p.isLivre ? "#00D492" : "#2B7FFF" }}
                      onClick={() => openLead(p.lead, `${p.nome}, calculadora`)}
                    >
                      {p.cta}
                    </Button>
                    <p className="mt-3 text-center text-[10px] font-medium text-slate-400">
                      Valores referentes à assinatura anual. Renovação automática.
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-white/[0.07]">
        <div className="mx-auto max-w-3xl px-4 py-16 sm:px-6 sm:py-20">
          <p className="text-center text-[11px] font-black uppercase tracking-[0.24em] text-fc-teal sm:text-xs">
            PERGUNTAS FREQUENTES
          </p>
          <h2 className="mt-4 text-center text-2xl font-black leading-tight tracking-tight sm:text-4xl">
            Dúvidas sobre o <span className="text-fc-teal">FEO para Clínicas</span>
          </h2>

          <Accordion type="single" collapsible className="mt-10 space-y-3">
            {B2B_FAQ.map((f, i) => (
              <AccordionItem
                key={f.q}
                value={`faq-${i}`}
                className="overflow-hidden rounded-2xl border border-white/[0.08] bg-white/[0.04] px-5 transition-colors duration-300 hover:border-fc-teal/25"
              >
                <AccordionTrigger className="py-5 text-left text-sm font-bold leading-snug hover:no-underline sm:text-base">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="pb-5 text-sm leading-relaxed text-white/70 whitespace-pre-line">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>

          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "FAQPage",
                mainEntity: B2B_FAQ.map((f) => ({
                  "@type": "Question",
                  name: f.q,
                  acceptedAnswer: { "@type": "Answer", text: f.a },
                })),
              }),
            }}
          />
        </div>
      </section>

      {/* CTA FINAL */}
      <section className="relative overflow-hidden border-t border-white/[0.07]">
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              "radial-gradient(60% 70% at 50% 110%, rgba(63,213,201,0.18) 0%, transparent 70%)",
          }}
        />
        <div className="relative mx-auto max-w-3xl px-4 py-16 text-center sm:px-6 sm:py-20">
          <h2 className="text-2xl font-black leading-tight tracking-tight sm:text-4xl">
            Vamos desenhar o programa da sua clínica
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-relaxed text-white/70 sm:text-base">
            Preencha seus dados e nossa equipe entra em contato pelo WhatsApp para agendar uma reunião
            online e apresentar o programa em detalhes.
          </p>
          <Button
            size="lg"
            asChild
            className="mt-8 rounded-full bg-white px-8 font-bold text-fc-navy shadow-[0_16px_48px_-16px_rgba(255,255,255,0.25)] transition-all hover:bg-white/90 hover:shadow-[0_20px_58px_-16px_rgba(255,255,255,0.35)]"
          >
            <a href="#planos">Tenho interesse <ArrowRight className="ml-1.5 h-4 w-4" /></a>
          </Button>
        </div>
      </section>


      <MinimalFooter />

      <B2bLeadDialog plano={openPlano} onClose={() => setOpenPlano(null)} />
    </div>
  );
}

